import React, { useState, useEffect, useCallback } from 'react';

interface BinanceFuturesOrderRecord {
  id: number;
  symbol: string;
  trade_id: number;
  order_id: number;
  side: string;
  position_side: string;
  price: number | null;
  qty: number | null;
  quote_qty: number | null;
  realized_pnl: number | null;
  commission: number | null;
  commission_asset: string;
  trade_timestamp: string;
  collected_at: number;
}

interface Props {
  title: string;
  startTime?: string;
  endTime?: string;
}

const BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '/mercury-backend';

const BtBinanceFuturesTradeHistory: React.FC<Props> = ({ title, startTime, endTime }) => {
  const [records, setRecords] = useState<BinanceFuturesOrderRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  const filterByTime = (data: BinanceFuturesOrderRecord[]) => {
    if (!startTime || !endTime) return data;
    const startMs = Date.parse(startTime.endsWith('Z') ? startTime : startTime + 'Z');
    const endMs = Date.parse(endTime.endsWith('Z') ? endTime : endTime + 'Z');
    return data.filter(r => {
      const t = Date.parse(r.trade_timestamp.replace(' ', 'T') + 'Z');
      return t >= startMs && t <= endMs;
    });
  };

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE}/bt/binance-futures-orders/history?limit=500`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRecords(filterByTime(json.data || []));
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Failed to fetch';
      setError(raw === 'Failed to fetch' ? 'mercury-backend 서버에 연결할 수 없습니다.' : raw);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, endTime]);

  const handleImport = useCallback(async () => {
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      const res = await fetch(`${BASE}/bt/binance-futures-orders/import?limit=500`, { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setRecords(filterByTime(json.data || []));
      setImportResult(`${json.imported}건 저장 (전체 ${json.fetched}건 조회)`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, endTime]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const totalPnl = records.reduce((s, r) => s + (r.realized_pnl ?? 0), 0);
  const fmtTime = (t: string) => t ? t.replace('T', ' ').replace('Z', '').slice(0, 19) : '-';
  const fmtNum = (v: number | null, d = 4) =>
    v != null ? v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d }) : '-';

  return (
    <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h4 style={{ margin: 0, color: '#495057' }}>{title}</h4>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {importResult && (
            <span style={{ fontSize: '12px', color: '#198754' }}>{importResult}</span>
          )}
          <button
            onClick={handleImport}
            disabled={importing}
            style={{
              padding: '4px 12px', borderRadius: '4px',
              border: '1px solid #0d6efd',
              backgroundColor: importing ? '#ccc' : '#0d6efd',
              color: '#fff', fontSize: '12px',
              cursor: importing ? 'not-allowed' : 'pointer',
            }}
          >
            {importing ? '임포트중...' : 'Binance 체결내역 임포트'}
          </button>
          <button
            onClick={fetchRecords}
            disabled={loading}
            style={{
              padding: '4px 12px', borderRadius: '4px',
              border: '1px solid #ced4da', backgroundColor: '#fff',
              fontSize: '12px', cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? '로딩중...' : '새로고침'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '15px' }}>
        {/* 왼쪽: 내 실제 체결내역 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h5 style={{ margin: '0 0 8px 0', color: '#0d6efd', fontSize: '13px' }}>
            Binance Futures 실제 체결내역
            <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '8px' }}>
              ({records.length}건)
            </span>
            {records.length > 0 && (
              <span style={{
                fontWeight: 'normal', marginLeft: '8px',
                color: totalPnl > 0 ? '#dc3545' : totalPnl < 0 ? '#0d6efd' : '#495057',
              }}>
                실현손익 {totalPnl > 0 ? '+' : ''}{totalPnl.toFixed(4)} USDT
              </span>
            )}
          </h5>
          {error && (
            <div style={{ padding: '8px', backgroundColor: '#fff5f5', color: '#dc3545', borderRadius: '4px', fontSize: '13px', marginBottom: '8px' }}>
              {error}
            </div>
          )}
          {records.length === 0 && !loading && !error ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d', fontSize: '13px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              체결 데이터가 없습니다. "Binance 체결내역 임포트" 버튼을 눌러주세요.
            </div>
          ) : (
            <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#d0e7ff', position: 'sticky', top: 0 }}>
                    <th style={thStyle}>방향</th>
                    <th style={thStyle}>심볼</th>
                    <th style={thStyle}>가격</th>
                    <th style={thStyle}>수량</th>
                    <th style={thStyle}>실현손익</th>
                    <th style={thStyle}>수수료</th>
                    <th style={thStyle}>시각</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ ...tdStyle, color: r.side === 'BUY' ? '#dc3545' : '#0d6efd', fontWeight: 600 }}>
                        {r.side}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{r.symbol}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtNum(r.price, 2)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtNum(r.qty, 4)}</td>
                      <td style={{
                        ...tdStyle, textAlign: 'right',
                        color: (r.realized_pnl ?? 0) > 0 ? '#dc3545' : (r.realized_pnl ?? 0) < 0 ? '#0d6efd' : '#000',
                      }}>
                        {r.realized_pnl != null ? ((r.realized_pnl > 0 ? '+' : '') + r.realized_pnl.toFixed(4)) : '-'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#6c757d' }}>
                        {r.commission != null ? r.commission.toFixed(6) : '-'} {r.commission_asset}
                      </td>
                      <td style={{ ...tdStyle, fontSize: '11px' }}>{fmtTime(r.trade_timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 오른쪽: 실제 매매기록 (구축예정) */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h5 style={{ margin: '0 0 8px 0', color: '#495057', fontSize: '13px' }}>
            실제 매매기록 (bt_actual_binance_futures_trade)
            <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '8px' }}>
              구축예정
            </span>
          </h5>
          <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d', fontSize: '13px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
            bt_actual_binance_futures_trade 테이블 구축 예정
          </div>
        </div>
      </div>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '6px 8px', textAlign: 'left', fontWeight: 600,
  borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '5px 8px', whiteSpace: 'nowrap', color: '#000',
};

export default BtBinanceFuturesTradeHistory;
